export default function Hero() {
  const handleAnchor = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20abstract%20composition%20with%20warm%20amber%20and%20deep%20crimson%20tones%2C%20soft%20film%20grain%20texture%2C%20subtle%20lens%20flare%2C%20geometric%20light%20rays%20emanating%20from%20center%2C%20dark%20theater%20atmosphere%20with%20dramatic%20lighting%2C%20elegant%20minimal%20cinema%20aesthetic%2C%20no%20text%20no%20people%2C%20atmospheric%20depth&width=1920&height=1080&seq=hero-bg-jiujiu&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left - Text */}
          <div className="flex-1 w-full text-center lg:text-left">
            {/* Tag */}
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/80 text-background-50 text-xs font-medium tracking-wide mb-5 whitespace-nowrap">
              海量片库 · 高清不缓冲
            </span>

            {/* H1 */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
              久久影视
              <br className="sm:hidden" />
              <span className="text-primary-300"> · </span>
              高清影视免费在线观看
            </h1>

            {/* Slogan */}
            <p className="text-lg md:text-xl text-background-100/90 font-medium mb-6">
              聚合全网高清影视资源，零成本畅享视听盛宴
            </p>

            {/* Intro */}
            <p className="text-sm md:text-base text-background-200/80 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
              久久影视汇聚海内外热门电影、电视剧、综艺、动漫等影视内容，
              每日持续更新全网最新资源。无论是院线大片还是经典老片，
              热门新剧还是怀旧佳作，在这里都能轻松找到。
              无需注册、无需付费，打开即看，1080P 高清画质搭配流畅播放体验，
              让你随时随地沉浸式观影。
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 md:gap-10 mb-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-300">
                  200+
                </div>
                <div className="text-xs text-background-200/70 mt-1">日均更新（部）</div>
              </div>
              <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-300">
                  10万+
                </div>
                <div className="text-xs text-background-200/70 mt-1">海量片库</div>
              </div>
              <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-300">
                  1080P
                </div>
                <div className="text-xs text-background-200/70 mt-1">高清画质</div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <button
                onClick={() => handleAnchor('#ranking')}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                <i className="ri-play-circle-fill mr-2"></i>
                开始观看
              </button>
              <button
                onClick={() => handleAnchor('#about')}
                className="px-8 py-3 border border-background-50/30 hover:border-background-50/60 text-background-50 font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </button>
            </div>
          </div>

          {/* Right - Visual */}
          <div className="flex-1 w-full max-w-md lg:max-w-none flex items-center justify-center">
            <div className="relative w-[280px] h-[370px] sm:w-[340px] sm:h-[450px] md:w-[400px] md:h-[530px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Artistic%20cinematic%20movie%20poster%20collage%20with%20warm%20amber%20and%20gold%20lighting%2C%20abstract%20film%20strip%20elements%20floating%2C%20classic%20movie%20clapperboard%20and%20vintage%20projector%20silhouettes%2C%20soft%20dramatic%20spotlight%20effect%2C%20luxurious%20velvet%20curtain%20texture%20at%20edges%2C%20elegant%20theater%20aesthetic%2C%20rich%20dark%20background%20with%20warm%20highlights%2C%20no%20text%20no%20people%2C%20sophisticated%20cinema%20art&width=800&height=1060&seq=hero-visual-jiujiu&orientation=portrait"
                alt="久久影视高清观影体验"
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              {/* Decorative badge */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 bg-background-950/60 backdrop-blur-sm rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-play-fill text-background-50 text-sm"></i>
                </div>
                <div>
                  <div className="text-background-50 text-xs font-medium">免费高清 · 流畅不卡</div>
                  <div className="text-background-300 text-[10px]">打开即看，无需等待</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}